// SINGLE SOURCE OF TRUTH for the public-site theme presets (owner-approved
// dedup 2026-08-18). Previously the same list lived twice — the picker
// (ThemeAppearance) and the renderer (SportsPublicPage) — and a preset added
// to one but not the other would silently fall back to the default.
//
// Adding a preset = ONE entry in THEMES below. The picker derives its grid
// from THEME_LIST (map insertion order = display order), the public pages
// look up by key. Keys are saved in tenant settings — NEVER rename/remove;
// reordering is safe.

// Text color that stays readable on any accent — bright accents (yellow, volt,
// lime) get dark text, deep reds/blues get white. Guards custom accents later.
export const contrastOn = (hex) => {
  try {
    const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 0.45 ? '#1A1A1A' : '#FFFFFF';
  } catch { return '#FFFFFF'; }
};

const hexGlow = (hex, a = 0.25) => {
  try {
    const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
    return `rgba(${r},${g},${b},${a})`;
  } catch { return 'rgba(0,0,0,0)'; }
};

// Theme schema = the ORIGINAL premium tokens (bg/surface/border/primary/
// primaryDark/glow/text/muted/subtle/gradient) PLUS the 7 short aliases
// (card/line/txt/mut/acc/accd) the 2026-07 landing uses. Gradients and glow
// are the depth that makes the dark themes read premium — never strip them.
// `desc` is the picker's one-line pitch; it rides along untouched.
export const mkTheme = (t) => ({
  ...t,
  card: t.surface, line: t.border, txt: t.text, mut: t.muted,
  acc: t.primary, accd: t.accd || contrastOn(t.primary),
});

export const THEMES = {
  // 2026-07 presets — sporty signal accents on calm canvases.
  'Court Flame': mkTheme({
    name: 'Court Flame', desc: 'Forest green + flame — the new default',
    bg: '#0E2318', surface: '#16301F', border: '#24422F',
    primary: '#FF6B35', primaryDark: '#D9531F', glow: hexGlow('#FF6B35'),
    text: '#F2F7EF', muted: '#9DB5A4', subtle: '#6E8A78',
    gradient: 'linear-gradient(135deg, #FF6B35 0%, #FFB74D 100%)', accd: '#2B1000',
  }),
  'Championship Yellow': mkTheme({
    name: 'Championship Yellow', desc: 'Club green + tennis-ball yellow',
    bg: '#1B4025', surface: '#245231', border: '#356844',
    primary: '#FFD60A', primaryDark: '#D9B300', glow: hexGlow('#FFD60A', 0.22),
    text: '#F4F1DC', muted: '#A3C2A5', subtle: '#729678',
    gradient: 'linear-gradient(135deg, #FFD60A 0%, #FFE566 100%)', accd: '#3A2E00',
  }),
  'Court Green': mkTheme({
    name: 'Court Green', desc: 'Calm forest green + soft lime',
    bg: '#0E2318', surface: '#16301F', border: '#24422F',
    primary: '#C9F24E', primaryDark: '#A3CC2E', glow: hexGlow('#C9F24E', 0.2),
    text: '#F2F7EF', muted: '#9DB5A4', subtle: '#6E8A78',
    gradient: 'linear-gradient(135deg, #C9F24E 0%, #E2FF8A 100%)', accd: '#1A2E0A',
  }),
  'Midnight Red': mkTheme({
    name: 'Midnight Red', desc: 'Navy + signal red — classic sporty',
    bg: '#101C2E', surface: '#182842', border: '#26395A',
    primary: '#E02339', primaryDark: '#B31A2D', glow: hexGlow('#E02339'),
    text: '#F1F4F9', muted: '#97A8C0', subtle: '#66788F',
    gradient: 'linear-gradient(135deg, #E02339 0%, #FF5A6A 100%)', accd: '#FFFFFF',
  }),
  'Wimbledon Ball': mkTheme({
    name: 'Wimbledon Ball', desc: 'Heritage purple + ball green',
    bg: '#221A36', surface: '#2E2449', border: '#413364',
    primary: '#DDF247', primaryDark: '#B8CC2E', glow: hexGlow('#DDF247', 0.22),
    text: '#F4F1FA', muted: '#AB9EC8', subtle: '#7C6F9B',
    gradient: 'linear-gradient(135deg, #DDF247 0%, #EFFF85 100%)', accd: '#2A3305',
  }),
  'Volt Mono': mkTheme({
    name: 'Volt Mono', desc: 'Charcoal + volt — night games',
    bg: '#0C0C0C', surface: '#161616', border: '#2A2A2A',
    primary: '#CEFF00', primaryDark: '#A8D000', glow: hexGlow('#CEFF00', 0.22),
    text: '#F4F2EA', muted: '#9E9C94', subtle: '#6E6C64',
    gradient: 'linear-gradient(135deg, #CEFF00 0%, #E4FF66 100%)', accd: '#202B00',
  }),
  'Clay Signal Red': mkTheme({
    name: 'Clay Signal Red', desc: 'Warm light — clay court classic',
    bg: '#F7F0E3', surface: '#FFFFFF', border: '#E8DAC2',
    primary: '#D7263D', primaryDark: '#AB1B2F', glow: hexGlow('#D7263D', 0.16),
    text: '#2F2A1F', muted: '#857A64', subtle: '#A99D85',
    gradient: 'linear-gradient(135deg, #D7263D 0%, #F05365 100%)', accd: '#FFFFFF',
  }),
  'Daylight': mkTheme({
    name: 'Daylight', desc: 'Clean light — family venues',
    bg: '#FAFAF5', surface: '#FFFFFF', border: '#E3E1D5',
    primary: '#2F6B3A', primaryDark: '#245229', glow: hexGlow('#2F6B3A', 0.14),
    text: '#1E2A1F', muted: '#6E7B6E', subtle: '#98A398',
    gradient: 'linear-gradient(135deg, #2F6B3A 0%, #4C9A5C 100%)', accd: '#FFFFFF',
  }),
  // Legacy presets — ORIGINAL values verbatim.
  'Velocity Red': mkTheme({
    name: 'Velocity Red', desc: 'High energy — racket sports',
    bg: '#0A0A0A', surface: '#141414', border: '#2A1A1A',
    primary: '#FF2D55', primaryDark: '#CC2244', glow: 'rgba(255,45,85,0.25)',
    text: '#FFFFFF', muted: '#A3A3A3', subtle: '#737373',
    gradient: 'linear-gradient(135deg, #FF1A3E 0%, #FF6B35 100%)', accd: '#FFFFFF',
  }),
  'Neon Court': mkTheme({
    name: 'Neon Court', desc: 'Fresh green — basketball / volleyball',
    bg: '#040D07', surface: '#081208', border: '#0A2A14',
    primary: '#00E676', primaryDark: '#00B85E', glow: 'rgba(0,230,118,0.25)',
    text: '#FFFFFF', muted: '#9AE6B0', subtle: '#5A8A6A',
    gradient: 'linear-gradient(135deg, #00E676 0%, #76FF03 100%)', accd: '#04351C',
  }),
  'Midnight Pro': mkTheme({
    name: 'Midnight Pro', desc: 'Bold lime — universal sports',
    bg: '#0A0A0A', surface: '#141414', border: '#1E2814',
    primary: '#C6FF00', primaryDark: '#9ACA00', glow: 'rgba(198,255,0,0.22)',
    text: '#FFFFFF', muted: '#C6D4A3', subtle: '#8A9A6A',
    gradient: 'linear-gradient(135deg, #C6FF00 0%, #76FF03 100%)', accd: '#26300A',
  }),
  'Court Orange': mkTheme({
    name: 'Court Orange', desc: 'Warm orange — basketball / futsal',
    bg: '#0A0807', surface: '#150F0A', border: '#2A1A0A',
    primary: '#FF6D00', primaryDark: '#CC5800', glow: 'rgba(255,109,0,0.25)',
    text: '#FFFFFF', muted: '#E0B090', subtle: '#A07050',
    gradient: 'linear-gradient(135deg, #FF6D00 0%, #FFB74D 100%)', accd: '#2B1200',
  }),
  'Electric Blue': mkTheme({
    name: 'Electric Blue', desc: 'Cool blue — futsal / swimming',
    bg: '#030712', surface: '#07101E', border: '#0A1A35',
    primary: '#2979FF', primaryDark: '#1565C0', glow: 'rgba(41,121,255,0.25)',
    text: '#FFFFFF', muted: '#90B8F0', subtle: '#5080B0',
    gradient: 'linear-gradient(135deg, #2979FF 0%, #40C4FF 100%)', accd: '#FFFFFF',
  }),
};

// Picker-shaped view of THEMES — id/label/desc/primary/bg in display order.
export const THEME_LIST = Object.entries(THEMES).map(([id, t]) => ({
  id, label: id, desc: t.desc || '', primary: t.primary, bg: t.bg,
}));
